import { useState } from 'react'
import { router } from '@inertiajs/react'
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { CalendarEventsSection } from '@/components/shared/CalendarEventsSection'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { DetailGrid } from '@/components/backoffice/DetailGrid'
import { DocumentUploader } from '@/components/shared/DocumentUploader'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { BackofficeBusinessTaskDetailed, FieldDefinition, FieldValue, DocumentAttachment } from '@/types'

type Template = { id: string; name: string; file_url: string | null; created_at: string }

type CalendarEvent = {
  id: string
  title: string
  due_date: string
  source: string
  completed: boolean
  complete_path?: string
  delete_path?: string
}

type Props = {
  task: BackofficeBusinessTaskDetailed
  fields: FieldDefinition[]
  field_values: FieldValue[]
  documents: DocumentAttachment[]
  templates: Template[]
  calendar_events: CalendarEvent[]
}


function getFieldValueDisplay(field: FieldDefinition, fieldValues: FieldValue[]): string {
  const fv = fieldValues.find(v => v.field_id === field.id)
  if (!fv) return '—'
  if (fv.option_labels && fv.option_labels.length > 0) return fv.option_labels.join(', ')
  if (fv.value != null) return String(fv.value)
  return '—'
}

export default function BusinessTasksShow({ task, fields, field_values, documents, templates, calendar_events }: Props) {
  const [completeState, setCompleteState] = useState<'idle' | 'form' | 'submitting'>('idle')
  const [notes, setNotes] = useState(task.completion_notes || '')

  const handleComplete = (e: React.FormEvent) => {
    e.preventDefault()
    setCompleteState('submitting')
    router.post(`/backoffice/business_tasks/${task.id}/completion`, { completion: { notes } }, {
      onFinish: () => setCompleteState('idle')
    })
  }

  return (
    <BackofficeLayout
      title={task.type_name}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Tasks', href: '/backoffice/business_tasks' },
        { label: task.type_name },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{task.type_name}</h1>
            <StatusBadge status={task.status} />
          </div>
          <p className="text-sm text-muted-foreground">{task.business_name} · {task.account_name}</p>
        </div>

        {task.status !== 'completed' && (
          <div className="mb-4">
            {completeState === 'idle' ? (
              <Button size="sm" onClick={() => setCompleteState('form')} className="gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Mark Complete
              </Button>
            ) : (
              <form onSubmit={handleComplete} className="border rounded-lg p-4 bg-muted/30 space-y-3">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <h3 className="text-sm font-medium">Complete Task</h3>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="notes">Completion Notes (Optional)</Label>
                  <Textarea 
                    id="notes"
                    placeholder="Add any internal notes about the completion..." 
                    value={notes} 
                    onChange={e => setNotes(e.target.value)} 
                    className="h-20"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button type="submit" size="sm" disabled={completeState === 'submitting'}>
                    {completeState === 'submitting' ? 'Saving...' : 'Confirm Completion'}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setCompleteState('idle')}>
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}

        <DetailGrid items={[
          { label: 'Assigned By', value: task.assigned_by || '—' },
          { label: 'Due Date', value: task.due_date
            ? <span className={task.overdue ? 'text-red-600 font-medium flex items-center gap-1' : ''}>
                {task.overdue && <AlertCircle className="w-3 h-3" />}
                {new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            : '—'
          },
          { label: 'Status', value: <StatusBadge status={task.status} /> },
          { label: 'Created', value: new Date(task.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) },
          ...(task.responded_at ? [{ label: 'Responded', value: new Date(task.responded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }] : []),
          ...(task.completed_at ? [{ label: 'Completed', value: `${new Date(task.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}${task.completed_by ? ` by ${task.completed_by}` : ''}` }] : []),
        ]} />

        {task.instructions && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Instructions</h2>
            </div>
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.instructions}</p>
            </div>
          </section>
        )}

        {task.type_description && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Task Description</h2>
            </div>
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.type_description}</p>
            </div>
          </section>
        )}

        {field_values.length > 0 && fields.length > 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Form Response</h2>
            </div>
            <div className="px-4 py-4 space-y-3">
              {fields.map(field => (
                <div key={field.id} className="flex justify-between items-start text-sm">
                  <span className="text-muted-foreground">{field.label}</span>
                  <span className="text-right max-w-[60%]">{getFieldValueDisplay(field, field_values)}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {task.status === 'pending' && fields.length > 0 && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                Awaiting Response
              </h2>
            </div>
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground">The business has not yet responded to this task.</p>
            </div>
          </section>
        )}

        {task.completion_notes && (
          <section className="border rounded-lg">
            <div className="px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Completion Notes</h2>
            </div>
            <div className="px-4 py-4">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{task.completion_notes}</p>
            </div>
          </section>
        )}

        <DocumentUploader
          title="Documents"
          documents={documents}
          uploadPath={`/backoffice/business_tasks/${task.id}/documents`}
          deletePath={(docId) => `/backoffice/business_tasks/${task.id}/documents/${docId}`}
        />

        {/* Calendar Events */}
        <CalendarEventsSection events={calendar_events || []} />
      </div>
    </BackofficeLayout>
  )
}
